import {
  differenceInCalendarDays,
  format,
  formatISO,
  intervalToDuration,
  isValid,
  parse,
  parseISO,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { fr } from 'date-fns/locale'

// Fonction pour formater la date avec gestion des formats multiples
export const formatDate = (rawDate?: string): string => {
  if (!rawDate) return 'false'
  const cleanedDateString = rawDate.toString().includes('(')
    ? rawDate.toString().replace(/\(.*\)/, '')
    : rawDate

  // Liste des formats possibles (ajoutez autant de formats que nécessaire)
  const possibleFormats = [
    'yyyy-MM-dd', // Format simple
    'yyyy-MM-dd-HH-mm', // Format avec heure
    'yyyy-MM-dd HH:mm:ss', // Format avec secondes
    'yyyy-MM-ddTHH:mm:ss.SSSZ', // Format ISO 860 1
    "yyyy-MM-dd'T'HH:mm:ssXXX", // Format ISO 8601 avec fuseau horaire
    'MM/dd/yyyy', // Format américain
    'dd/MM/yyyy', // Format européen
    "E MMM dd yyyy HH:mm:ss 'GMT'xxxx", // Format de date JS
  ]

  let parsedDate: Date | null = null

  // Tester chaque format jusqu'à ce qu'on trouve un format valide
  for (const formatStr of possibleFormats) {
    parsedDate = parse(cleanedDateString, formatStr, new Date())

    // Si la date est valide, on arrête et on formate
    if (isValid(parsedDate)) {
      return format(parsedDate, 'dd/MM/yyyy', { locale: fr })
    }

    // Si la date est au format ISO 8601 (avec fuseau horaire), utiliser parseISO
    try {
      parsedDate = parseISO(cleanedDateString)
      if (isValid(parsedDate)) {
        return format(parsedDate, 'dd/MM/yyyy', { locale: fr })
      }
    } catch (e) {
      console.error('Erreur de parsing ISO:', e)
    }
  }

  // Si aucune date valide n'est trouvée, on retourne une date invalide
  return 'false'
}

export const formatDateForOdoo = (value: Date) => {
  const date = value.toLocaleString('en-CA', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const hour = value.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  return date + ' ' + hour
}

export const parseUtcDate = (dateString: string, formatString: string): string => {
  const parsedDate = parse(dateString, formatString, new Date())
  const utcDate = new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      parsedDate.getHours(),
      parsedDate.getMinutes(),
      parsedDate.getSeconds(),
      parsedDate.getMilliseconds(),
    ),
  )

  return formatISO(utcDate)
}

/** start/stop événements : ISO, Odoo `yyyy-MM-dd HH:mm:ss`, etc. → ISO pour le calendrier. Ne lance pas. */
export function normalizeEventInstantIso(raw: string | undefined | null): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''

  const withT = s.includes('T') ? s : s.replace(' ', 'T')
  const fromIso = parseISO(withT)
  if (isValid(fromIso)) return formatISO(fromIso)

  const odooLocal = parse(s, 'yyyy-MM-dd HH:mm:ss', new Date())
  if (isValid(odooLocal)) {
    const utcDate = new Date(
      Date.UTC(
        odooLocal.getFullYear(),
        odooLocal.getMonth(),
        odooLocal.getDate(),
        odooLocal.getHours(),
        odooLocal.getMinutes(),
        odooLocal.getSeconds(),
        odooLocal.getMilliseconds(),
      ),
    )
    if (isValid(utcDate)) return formatISO(utcDate)
  }

  const fallback = new Date(s)
  return isValid(fallback) ? formatISO(fallback) : ''
}

export const removeTimeZone = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd HH:mm')
}

export const removeTime = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

const convertTimeToNumber = (input: any): number => {
  return input.hours + input.minutes / 60
}

export const calculateDurationWithFormat = (date1: Date, date2: Date) => {
  // Calculer la durée
  const duration = intervalToDuration({
    start: new Date(date1),
    end: new Date(date2),
  })

  // Formatter en HH:mm
  const hours = (duration.days ?? 0) * 24 + (duration.hours ?? 0)
  const minutes = duration.minutes ?? 0

  return convertTimeToNumber({ hours: hours, minutes: minutes })
}

export const formatDateRangeToPopoverText = (start: string, end: string) => {
  const formatTime = (date: Date) =>
    new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris',
    }).format(date)

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Europe/Paris',
    }).format(date)

  const startDate = new Date(start)
  const endDate = new Date(end)

  if (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  ) {
    return `de ${formatTime(startDate)} à ${formatTime(endDate)}`
  } else {
    return `du ${formatDate(startDate)} à ${formatTime(startDate)} au ${formatDate(endDate)} à ${formatTime(endDate)}`
  }
}

export const dateComparator = (date1: string, date2: string) => {
  const date1Number = dateToComparableNumber(date1)
  const date2Number = dateToComparableNumber(date2)
  if (date1Number === null && date2Number === null) {
    return 0
  }
  if (date1Number === null) {
    return -1
  }
  if (date2Number === null) {
    return 1
  }
  return date1Number - date2Number
}

function dateToComparableNumber(date: string) {
  if (!date) return null

  // Regex pour capturer la date et éventuellement l'heure
  const regex = /(\d{2}\/\d{2}\/\d{4})[^0-9]*(\d{2}:\d{2})?/

  // Chercher la première date et éventuellement l'heure
  const match = date.match(regex)

  if (!match) return null // Aucun format reconnu

  const extractedDate = match[1] // Date trouvée (DD/MM/YYYY)
  const extractedTime = match[2] || '00:00' // Heure trouvée ou 00:00 par défaut

  // Extraire jour, mois, année, heure et minutes
  const [day, month, year] = extractedDate.split('/').map(Number)
  const [hour, minute] = extractedTime.split(':').map(Number)

  return year * 100000000 + month * 1000000 + day * 10000 + hour * 100 + minute
}

export const areDatesOnSameDay = (firstDate: string | Date, secondDate: string | Date): boolean => {
  const firstDateObj = new Date(firstDate)
  const secondDateObj = new Date(secondDate)

  if (isNaN(firstDateObj.getTime()) || isNaN(secondDateObj.getTime())) {
    throw new Error('Une ou les deux dates sont invalides')
  }

  return (
    firstDateObj.getFullYear() === secondDateObj.getFullYear() &&
    firstDateObj.getMonth() === secondDateObj.getMonth() &&
    firstDateObj.getDate() === secondDateObj.getDate()
  )
}

export const formatDateForDisplay = (date: Date) => {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export const getTimeRemaining = (date: Date) => {
  return differenceInCalendarDays(date, new Date())
}

/** Clé YYYY-MM-DD au calendrier local (cohérent avec getLongTimeFr / « aujourd'hui »). */
export const localCalendarDateKey = (raw: Date | string | undefined | null): string | null => {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  }
  const d = raw instanceof Date ? raw : new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const getLongTimeFr = (date: Date) => {
  return date.toLocaleString('fr-FR', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Temps relatif compact « il y a X s / min / h / j » (FR).
 * `nowMs` est injectable pour permettre un rafraîchissement réactif (tick).
 * Renvoie null si la date est absente ou invalide.
 */
export const formatTimeAgo = (
  raw: Date | string | number | undefined | null,
  nowMs: number = Date.now(),
): string | null => {
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  const diffSec = Math.max(0, Math.round((nowMs - d.getTime()) / 1000))
  if (diffSec < 5) return 'à l’instant'
  if (diffSec < 60) return `il y a ${diffSec} s`
  const min = Math.floor(diffSec / 60)
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const j = Math.floor(h / 24)
  return `il y a ${j} j`
}
